import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { UntypedFormArray, UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { InvoiceAmountCalc } from '@app/core/models/invoices/invoice-amount-calc.model';
import { InvoiceDetailModel } from '@app/core/models/invoices/invoice-detail.model';
import { InvoiceExpenseDetail } from '@app/core/models/invoices/invoice-expense.model';
import { InvoiceParameters } from '@app/core/models/invoices/invoice-parameters.model';
import { ExpenseFamilyTypeRef } from '@app/core/models/referentials/expense-family-type.model';
import { VatRef } from '@app/core/models/referentials/vat.model';
import { BuildFormGroupsService } from '@app/core/services/manageInvoice/build-form-groups.service';
import { CommonLibraryService } from '@app/core/services/manageInvoice/common-library.service';
import { ExpenseLibraryService } from '@app/core/services/manageInvoice/expense-library.service';
import * as _ from 'lodash';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'app-invoice-expense',
  templateUrl: './invoice-expense.component.html',
  styleUrls: ['./invoice-expense.component.scss'],
})
export class InvoiceExpenseComponent
  implements OnInit, OnDestroy, AfterViewInit
{
  unsubscribe$: Subject<boolean> = new Subject<boolean>();

  @Input() invoiceDetailModel: InvoiceDetailModel;
  @Input() invoiceDetailAmountCalc: InvoiceAmountCalc;
  @Input() invoiceDetailForm: UntypedFormGroup;

  @Input() dropDownSettingsVat: any;
  @Input() dropDownDataVat: VatRef[];

  @Input() dropDownSettingsExpenseFamilyType: any;
  @Input() dropDownDataExpenseFamilyType: ExpenseFamilyTypeRef[];

  @Input() invoiceParams: InvoiceParameters;
  @Input() isReadOnlyForm: boolean;
  @Input() isReadOnlyFormOrWaiting: boolean;
  @Input() submitted: boolean;

  @Input() editedDropdowns: string[];

  @Output() onExpensesChange: EventEmitter<UntypedFormArray> =
    new EventEmitter<UntypedFormArray>();
  @Output() onExpensesPatched: EventEmitter<UntypedFormArray> =
    new EventEmitter<UntypedFormArray>();

  subcriptValueChanges: boolean = false;

  constructor(
    public commonLibrary: CommonLibraryService,
    public formInvoiceBuilder: BuildFormGroupsService,
    public expenseLibrary: ExpenseLibraryService,
    private formBuilder: UntypedFormBuilder,
    private cdref: ChangeDetectorRef
  ) {}

  ngAfterViewInit(): void {
    setTimeout(() => {
      this.patchExpenses();
    });
    this.cdref.detectChanges();
  }

  ngOnInit(): void {
    this.setValueChangeSubscription();
  }

  setValueChangeSubscription() {
    this.subcriptValueChanges = this.InvoiceExpenses != undefined && this.InvoiceExpenses.length > 0;

    if (this.subcriptValueChanges){
      this.InvoiceExpenses.valueChanges
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(() => {
        this.onExpensesChange.emit(this.InvoiceExpenses);
      });
    }
  }

  get InvoiceExpenses() {
    return this.invoiceDetailForm.controls['InvoiceExpenses'] as UntypedFormArray;
  }

  patchExpenses() {
    if (
      this.invoiceDetailModel.Expenses &&
      this.invoiceDetailModel.Expenses?.length > 0
    ) {
      this.invoiceDetailModel.Expenses.forEach((x) => {
        this.InvoiceExpenses.push(this.addExpenseFormGroup(x));
      });
    } else {
      this.addFirstExpense();
    }

    this.formInvoiceBuilder.markFormGroupTouched(this.invoiceDetailForm);
    this.setSumExpenses();
    this.onExpensesPatched.emit(this.InvoiceExpenses);
  }

  addExpense() {
    const expense: InvoiceExpenseDetail = new InvoiceExpenseDetail();
    expense.CreatedAt = Date.now();
    expense.VatCode = '20';
    this.invoiceDetailModel.Expenses.push(expense);

    this.resetExpenseFamilyCode(expense.CreatedAt);

    this.InvoiceExpenses.push(this.addExpenseFormGroup(expense));
    this.formInvoiceBuilder.markFormGroupTouched(this.invoiceDetailForm);
    this.setValueChangeSubscription();
  }

  addFirstExpense() {
    const expense: InvoiceExpenseDetail = new InvoiceExpenseDetail();
    expense.CreatedAt = Date.now();
    expense.VatCode = '20';
    expense.AmountTaxExcluded = this.invoiceDetailModel.AmountTaxExcluded;

    this.invoiceDetailModel.Expenses.push(expense);
    this.InvoiceExpenses.push(this.addExpenseFormGroup(expense));

    this.onChangeExpenseVat(0, expense.CreatedAt, { Code: expense.VatCode });
    this.formInvoiceBuilder.markFormGroupTouched(this.invoiceDetailForm);
    this.setValueChangeSubscription();
  }

  addExpenseFormGroup(expense: InvoiceExpenseDetail): UntypedFormGroup {
    const selectedvat = this.dropDownDataVat.find(
      (x) => x.Code == expense.VatCode
    );
    return this.formInvoiceBuilder.buildExpenseFormGroup(
      expense,
      this.invoiceParams,
      this.isReadOnlyFormOrWaiting,
      this.isReadOnlyForm,
      selectedvat
    );
  }

  deleteExpense(index: number, createdAt: number) {
    const searchIndex: number = this.invoiceDetailModel.Expenses.findIndex(
      (x) => x.CreatedAt == createdAt
    );
    this.invoiceDetailModel.Expenses.splice(searchIndex, 1);
    this.InvoiceExpenses.removeAt(index);

    this.setSumExpenses();
    this.setValueChangeSubscription();
  }

  deleteAllExpense() {
    this.invoiceDetailModel.Expenses = [];
    this.invoiceDetailForm.controls['InvoiceExpenses'] = this.formBuilder.array(
      []
    );
    this.setValueChangeSubscription();
  }

  onChangeExpenseVat(
    index: number,
    createdAt: number,
    selectedVat?: { Code: string } | null
  ): void {
    this.editedDropdowns.push('ExpenseVat');
    const vatDatas = _.find(
      this.dropDownDataVat,
      (x) => x?.Code === selectedVat?.Code
    );

    var element = this.invoiceDetailModel.Expenses.find(
      (c) => c.CreatedAt == createdAt
    );
    element.VatCode = vatDatas?.Code;

    var item = this.InvoiceExpenses.at(index) as UntypedFormGroup;
    item.controls.ExpenseVat.setValue(vatDatas?.Code);

    this.updateExpenseAmount(index, createdAt, vatDatas?.Code, vatDatas?.Value);
  }

  calculatedVat(index: number, selectedFormGroup: UntypedFormGroup) {
    var element = this.expenseLibrary.calculatedVat(
      this.invoiceDetailModel,
      index,
      selectedFormGroup
    );
    if (selectedFormGroup.controls.ExpenseSelectedVat.value.length > 0) {
      const vatDatas = _.find(
        this.dropDownDataVat,
        (x) =>
          x?.Code ===
          selectedFormGroup.controls.ExpenseSelectedVat.value[0]?.Code
      );
      this.updateExpenseAmount(
        index,
        element.CreatedAt,
        vatDatas?.Code,
        vatDatas?.Value
      );
    } else {
      this.updateExpenseAmount(index, element.CreatedAt, null, null);
    }
  }

  resetExpenseFamilyCode(CreatedAt: number) {
    if (this.dropDownDataExpenseFamilyType.length == 1) {
      const familyDatas = this.dropDownDataExpenseFamilyType[0];

      const element = this.invoiceDetailModel.Expenses.find(
        (c) => c.CreatedAt == CreatedAt
      );
      element.FamilyCode = familyDatas?.FamilyCode;
      element.FamilyLabel = familyDatas?.FamilyLabel;
      element.FamilyAccount = familyDatas?.FamilyAccountingCode;
    }
  }

  resetExpensesFamilyCode(expenseFamilyType: ExpenseFamilyTypeRef[]) {
    let internalkeys = this.InvoiceExpenses as UntypedFormArray;
    this.dropDownDataExpenseFamilyType = expenseFamilyType;

    for (let i = 0; i < internalkeys.controls.length; i++) {
      const createdAt = (this.InvoiceExpenses.at(i) as UntypedFormGroup).controls
        .CreatedAt.value;
      const elementDM = (this.InvoiceExpenses.at(i) as UntypedFormGroup).controls
        .FamilyCode.value;

      const familyDatas = _.find(
        this.dropDownDataExpenseFamilyType,
        (x) => x?.FamilyCode === elementDM
      );
      const FamilyCodeExistInList =
        familyDatas != null || familyDatas != undefined;

      if (!FamilyCodeExistInList) {
        const currentFamilyCode =
          this.dropDownDataExpenseFamilyType.length == 1
            ? this.dropDownDataExpenseFamilyType[0].FamilyCode
            : null;
        this.onChangeExpenseFamilyType(i, createdAt, {
          FamilyCode: currentFamilyCode,
        });
      }
    }
  }

  onChangeExpenseFamilyType(
    index: number,
    createdAt: number,
    selectedFamilyType?: { FamilyCode: string } | null
  ): void {
    this.editedDropdowns.push('FamilyCode');
    const familyDatas = _.find(
      this.dropDownDataExpenseFamilyType,
      (x) => x?.FamilyCode === selectedFamilyType?.FamilyCode
    );
    var element = this.invoiceDetailModel.Expenses.find(
      (c) => c.CreatedAt == createdAt
    );
    element.FamilyCode = familyDatas?.FamilyCode;
    element.FamilyLabel = familyDatas?.FamilyLabel;
    element.FamilyAccount = familyDatas?.FamilyAccountingCode;

    var item = this.InvoiceExpenses.at(index) as UntypedFormGroup;
    item.controls.FamilyCode.setValue(
      familyDatas?.FamilyCode === undefined ? [] : [familyDatas?.FamilyCode]
    );
    item.controls.FamilyLabel.setValue(familyDatas?.FamilyLabel);
    item.controls.FamilyAccount.setValue(familyDatas?.FamilyAccountingCode);
  }

  onChangeExpenseLabelValue(event: any) {
    this.onExpensesChange.emit(this.InvoiceExpenses);
  }

  updateExpenseAmount(
    index: number,
    createdAt: number,
    vatcode: string,
    tvavalue: number
  ) {
    this.expenseLibrary.updateExpenseAmount(
      this.invoiceDetailModel,
      this.InvoiceExpenses,
      index,
      createdAt,
      vatcode,
      tvavalue
    );
    this.setSumExpenses();
    this.onExpensesChange.emit(this.InvoiceExpenses);
  }

  setSumExpenses() {
    this.expenseLibrary.setSumExpenses(
      this.invoiceDetailModel,
      this.invoiceDetailAmountCalc
    );
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next(true);
  }
}
